import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { trpc } from "../../lib/trpc";

export type LocationType =
  | "tesis" | "depo" | "bolge" | "raf"
  | "uretim_merkezi" | "uretim_hatti" | "unite" | "makina" | "mal_kabul" | "sevkiyat" | "karantina" | "scrap";

const TYPE_LABELS: Record<LocationType, string> = {
  tesis: "Tesis",
  depo: "Depo",
  bolge: "Bölge",
  raf: "Raf",
  uretim_merkezi: "Üretim Merkezi",
  uretim_hatti: "Üretim Hattı",
  unite: "Ünite",
  makina: "Makina",
  mal_kabul: "Mal Kabul",
  sevkiyat: "Sevkiyat",
  karantina: "Karantina / QC",
  scrap: "Scrap",
};

/** Üretim merkezi metadata'sı (Prisma Decimal'ler tRPC üzerinden string gelir). */
export interface CenterMeta {
  dailyCapacity: string | number | null;
  capacityUnit: string | null;
  shiftsPerDay: number | null;
  hoursPerShift: string | number | null;
  performancePct: string | number | null;
  maxConcurrentWo: number | null;
  allowStockProduction?: boolean | null;
  blockShortageProduction?: boolean | null;
  autoConsumeSemi?: boolean | null;
  autoProduceSemi?: boolean | null;
  notes: string | null;
}

interface LocationLite {
  id: string;
  code: string;
  name: string;
  type: LocationType;
  parentId: string | null;
  transferable?: boolean;
  productionCenter?: CenterMeta | null;
}

/** Stok tutabilen (dolayısıyla "transfer edilir" işaretlenebilen) lokasyon tipleri (api STOCK_LOC_TYPES ile senkron). */
const STOCK_LOC_TYPES = new Set<LocationType>(["depo", "raf", "bolge", "mal_kabul", "karantina", "sevkiyat", "scrap"]);

interface LocationFormModalProps {
  open: boolean;
  initial?: LocationLite | null;
  /** Pre-fill parentId for "Alt konum ekle" flow */
  presetParentId?: string | null;
  onClose: () => void;
}

interface FormState {
  code: string;
  name: string;
  type: LocationType;
  parentId: string;
  transferable: boolean;
  // Üretim merkezi alanları (text input — submit'te sayıya çevrilir)
  capDaily: string;
  capUnit: string;
  shifts: string;
  hoursPerShift: string;
  perfPct: string;
  maxWo: string;
  allowStockProduction: boolean;
  blockShortageProduction: boolean;
  autoConsumeSemi: boolean;
  autoProduceSemi: boolean;
  centerNotes: string;
}

const EMPTY: FormState = {
  code: "", name: "", type: "depo", parentId: "", transferable: false,
  capDaily: "", capUnit: "", shifts: "", hoursPerShift: "", perfPct: "", maxWo: "", allowStockProduction: false, blockShortageProduction: false, autoConsumeSemi: false, autoProduceSemi: false, centerNotes: "",
};

const s = (v: string | number | null | undefined): string => (v == null ? "" : String(v));

function toFormState(initial: LocationLite | null | undefined, presetParentId: string | null | undefined): FormState {
  if (!initial) {
    return { ...EMPTY, parentId: presetParentId ?? "" };
  }
  const c = initial.productionCenter;
  return {
    code: initial.code,
    name: initial.name,
    type: initial.type,
    parentId: initial.parentId ?? "",
    transferable: initial.transferable ?? false,
    capDaily: s(c?.dailyCapacity),
    capUnit: s(c?.capacityUnit),
    shifts: s(c?.shiftsPerDay),
    hoursPerShift: s(c?.hoursPerShift),
    perfPct: s(c?.performancePct),
    maxWo: s(c?.maxConcurrentWo),
    allowStockProduction: c?.allowStockProduction ?? false,
    blockShortageProduction: c?.blockShortageProduction ?? false,
    autoConsumeSemi: c?.autoConsumeSemi ?? false,
    autoProduceSemi: c?.autoProduceSemi ?? false,
    centerNotes: s(c?.notes),
  };
}

const numOrNull = (v: string): number | null => {
  const t = v.trim().replace(",", ".");
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};
const intOrNull = (v: string): number | null => {
  const n = numOrNull(v);
  return n == null ? null : Math.round(n);
};

// Ad → KISA kod segmenti (backend abbrevSegment ile birebir aynı kural).
// Tek kelime: ilk harf + ünlüsüzler (Soğutma→SGTM, cap 4). Çok kelime: baş harfler (Taşıyıcı Mavi Bant→TMB, cap 5).
const TR_ASCII: Record<string, string> = { "ç": "C", "Ç": "C", "ğ": "G", "Ğ": "G", "ı": "I", "İ": "I", "ö": "O", "Ö": "O", "ş": "S", "Ş": "S", "ü": "U", "Ü": "U" };
function abbrevSegment(name: string): string {
  const ascii = name.replace(/[çÇğĞıİöÖşŞüÜ]/g, (m) => TR_ASCII[m] ?? m).toUpperCase();
  const words = ascii.split(/[^A-Z0-9]+/).filter(Boolean);
  if (words.length === 0) return "";
  if (words.length === 1) {
    const w = words[0]!;
    let seg = w[0]! + w.slice(1).replace(/[AEIOU]/g, "");
    if (seg.length < 2) seg = w.slice(0, 3);
    return seg.slice(0, 4);
  }
  return words.map((w) => w[0]!).join("").slice(0, 5);
}

export function LocationFormModal({ open, initial, presetParentId, onClose }: LocationFormModalProps) {
  const isEdit = !!initial;
  const [state, setState] = useState<FormState>(EMPTY);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const qc = useQueryClient();

  useEffect(() => {
    if (open) {
      setState(toFormState(initial, presetParentId));
      setSubmitErr(null);
    } else {
      // Kapanışta sıfırla: bayat (type, parentId) çifti bir sonraki açılışta
      // otomatik tip düzeltme efektini yanlış tetikleyip taze state'i ezmesin.
      setState(EMPTY);
    }
  }, [open, initial, presetParentId]);

  const locsQ = trpc.location.list.useQuery(undefined, { staleTime: 30_000 });
  const createMut = trpc.location.create.useMutation();
  const updateMut = trpc.location.update.useMutation();
  const isPending = createMut.isPending || updateMut.isPending;

  const errorMessage = useMemo(() => {
    const mut = createMut.error ?? updateMut.error;
    if (submitErr) return submitErr;
    if (!mut) return null;
    const zod = (mut.data as { zodError?: { fieldErrors?: Record<string, string[]> } } | undefined)?.zodError?.fieldErrors;
    if (zod) {
      const msgs = Object.values(zod).flat().filter(Boolean) as string[];
      if (msgs.length) return msgs.join(" · ");
    }
    return mut.message ?? "Bilinmeyen hata";
  }, [submitErr, createMut.error, updateMut.error]);

  // Edit modu: kendi descendant'larını parent seçeneklerinden ele.
  // Makina yaprak — hiçbir lokasyonun üstü olamaz, listeden çıkar.
  const parentOptions = useMemo(() => {
    const all = (locsQ.data ?? []).filter((l) => l.type !== "makina");
    if (!initial) return all;
    const descendants = new Set<string>([initial.id]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const l of all) {
        if (l.parentId && descendants.has(l.parentId) && !descendants.has(l.id)) {
          descendants.add(l.id);
          changed = true;
        }
      }
    }
    return all.filter((l) => !descendants.has(l.id));
  }, [locsQ.data, initial]);

  // Seçili üst lokasyonun tipi — tip seçeneklerini bağlama göre kısıtlamak için.
  const parentType = useMemo<LocationType | null>(() => {
    if (!state.parentId) return null;
    const p = (locsQ.data ?? []).find((l) => l.id === state.parentId) as { type?: LocationType } | undefined;
    return p?.type ?? null;
  }, [state.parentId, locsQ.data]);

  // Ünite yalnız merkez/hat altına, makina yalnız ünite altına eklenebilir → tip listesini kısıtla.
  const typeOptions = useMemo<LocationType[]>(() => {
    const all = Object.keys(TYPE_LABELS) as LocationType[];
    let opts: LocationType[];
    if (parentType === "unite") opts = ["makina"]; // ünite altına yalnız makina
    else if (parentType === "makina") opts = []; // makina yaprak — altına lokasyon eklenemez
    else if (parentType === "uretim_merkezi" || parentType === "uretim_hatti") opts = all.filter((t) => t !== "makina"); // ünite dahil (makina hariç — o yalnız ünite altına)
    else opts = all.filter((t) => t !== "makina" && t !== "unite"); // ekipman tipleri yalnız merkez/hat/ünite altında
    // Düzenleme modu: mevcut tip her zaman listelensin (geçersiz konuma düşse bile görünsün).
    if (initial && !opts.includes(initial.type)) opts = [...opts, initial.type];
    return opts;
  }, [parentType, initial]);

  // Otomatik kod önizlemesi (yeni kayıt): <üst konum kodu>-<ad-slug>. Boş kod → backend bunu türetir.
  const derivedCode = useMemo(() => {
    if (isEdit) return "";
    const seg = abbrevSegment(state.name);
    if (!seg) return "";
    const parent = (locsQ.data ?? []).find((l) => l.id === state.parentId) as { code?: string } | undefined;
    const prefix = parent?.code ?? "";
    return (prefix ? `${prefix}-${seg}` : seg).slice(0, 50).replace(/-+$/, "");
  }, [isEdit, state.name, state.parentId, locsQ.data]);

  // Üst değişince mevcut tip geçersiz kaldıysa ilk geçerli tipe düş.
  useEffect(() => {
    if (!open) return;
    const first = typeOptions[0];
    if (first && !typeOptions.includes(state.type)) {
      setState((s) => ({ ...s, type: first }));
    }
  }, [typeOptions, open, state.type]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((s) => ({ ...s, [key]: value }));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitErr(null);
    if (!state.name.trim()) return setSubmitErr("Ad zorunlu.");

    const center = state.type === "uretim_merkezi" ? {
      dailyCapacity: numOrNull(state.capDaily),
      capacityUnit: state.capUnit.trim() || null,
      shiftsPerDay: intOrNull(state.shifts),
      hoursPerShift: numOrNull(state.hoursPerShift),
      performancePct: numOrNull(state.perfPct),
      maxConcurrentWo: intOrNull(state.maxWo),
      allowStockProduction: state.allowStockProduction,
      blockShortageProduction: state.blockShortageProduction,
      autoConsumeSemi: state.autoConsumeSemi,
      autoProduceSemi: state.autoProduceSemi,
      notes: state.centerNotes.trim() || null,
    } : undefined;

    try {
      if (isEdit && initial) {
        await updateMut.mutateAsync({
          id: initial.id,
          name: state.name,
          type: state.type,
          parentId: state.parentId || null,
          transferable: STOCK_LOC_TYPES.has(state.type) ? state.transferable : false,
          center,
        });
      } else {
        await createMut.mutateAsync({
          // Boş bırakılırsa backend üst konum + addan otomatik benzersiz kod türetir.
          code: state.code.trim() ? state.code.trim().toUpperCase() : undefined,
          name: state.name,
          type: state.type,
          parentId: state.parentId || null,
          transferable: STOCK_LOC_TYPES.has(state.type) ? state.transferable : false,
          center,
        });
      }
      await qc.invalidateQueries();
      onClose();
    } catch {
      /* err in banner */
    }
  }

  const isCenter = state.type === "uretim_merkezi";

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={isEdit ? "Lokasyon Düzenle" : "Yeni Lokasyon"}
      subtitle={isEdit ? `Kod: ${initial!.code}` : "Tesis hiyerarşisinde yeni konum"}
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={isPending}>İptal</button>
          <button type="submit" form="loc-form" className="btn-md primary" disabled={isPending}>
            {isPending ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Oluştur"}
          </button>
        </>
      }
    >
      <form id="loc-form" onSubmit={handleSubmit} noValidate>
        {errorMessage ? <div className="form-banner-err">{errorMessage}</div> : null}

        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="lf-code">
              Kod {isEdit ? null : <span style={{ fontWeight: 400, color: "var(--neutral-400)" }}>(opsiyonel)</span>}
            </label>
            <input
              id="lf-code"
              className="form-input"
              type="text"
              value={state.code}
              onChange={(e) => setField("code", e.target.value.toUpperCase())}
              placeholder={isEdit ? "" : derivedCode || "(otomatik)"}
              maxLength={50}
              autoComplete="off"
              disabled={isEdit}
            />
            {isEdit ? (
              <span className="help">Kod yarat sonrası değiştirilemez.</span>
            ) : state.code.trim() ? (
              <span className="help">Özel kod. Boş bırakırsanız ad ve üst konumdan otomatik türetilir.</span>
            ) : derivedCode ? (
              <span className="help">Otomatik: <b style={{ fontFamily: "var(--font-mono)" }}>{derivedCode}</b> · gerekirse numaralandırılır. Özel kod için buraya yazın.</span>
            ) : (
              <span className="help">Ad ve üst konumdan otomatik türetilir; dilerseniz özel kod yazın.</span>
            )}
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="lf-name">Ad <span className="req">*</span></label>
            <input
              id="lf-name"
              className="form-input"
              type="text"
              value={state.name}
              onChange={(e) => setField("name", e.target.value)}
              maxLength={200}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="lf-type">Tip <span className="req">*</span></label>
            <select
              id="lf-type"
              className="form-select"
              value={state.type}
              onChange={(e) => setField("type", e.target.value as LocationType)}
              required
            >
              {typeOptions.map((v) => (
                <option key={v} value={v}>{TYPE_LABELS[v]}</option>
              ))}
            </select>
            {state.type === "unite" ? (
              <span className="help">Ünite üretim merkezi/hattına bağlanır; altına makina eklenebilir (örn. Paketleme Ünitesi).</span>
            ) : state.type === "makina" ? (
              <span className="help">Makina yalnız bir ünite altına eklenir (örn. Otomatik Bantlama).</span>
            ) : null}
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="lf-parent">Üst Lokasyon</label>
            <select
              id="lf-parent"
              className="form-select"
              value={state.parentId}
              onChange={(e) => setField("parentId", e.target.value)}
            >
              <option value="">— Kök (üst yok) —</option>
              {parentOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {TYPE_LABELS[p.type as LocationType]}: {p.code} — {p.name}
                </option>
              ))}
            </select>
            <span className="help">Boş bırakılırsa root lokasyon olur (tipik: Tesis).</span>
          </div>
        </div>

        {STOCK_LOC_TYPES.has(state.type) ? (
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, cursor: "pointer", margin: "2px 0 12px" }}>
            <input type="checkbox" checked={state.transferable} onChange={(e) => setField("transferable", e.target.checked)} />
            <span><b>Transfer edilir</b> — bu depo, Depo Yönetimi'nde depolar-arası transfer planlamasında kaynak/hedef seçilebilir.</span>
          </label>
        ) : null}

        {isCenter ? (
          <div style={{ marginTop: 8, paddingTop: 12, borderTop: "1px solid var(--neutral-200)" }}>
            <div className="lbl" style={{ marginBottom: 8 }}>Üretim Merkezi — Kapasite &amp; Performans</div>
            <div className="form-row">
              <div className="form-field">
                <label className="lbl" htmlFor="lf-cap">Günlük Kapasite</label>
                <input id="lf-cap" className="form-input" type="text" inputMode="decimal" value={state.capDaily} onChange={(e) => setField("capDaily", e.target.value)} placeholder="örn. 1200" />
              </div>
              <div className="form-field">
                <label className="lbl" htmlFor="lf-capunit">Kapasite Birimi</label>
                <input id="lf-capunit" className="form-input" type="text" value={state.capUnit} onChange={(e) => setField("capUnit", e.target.value)} placeholder="kg/gün, adet/vardiya…" maxLength={30} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label className="lbl" htmlFor="lf-shifts">Vardiya / Gün</label>
                <input id="lf-shifts" className="form-input" type="text" inputMode="numeric" value={state.shifts} onChange={(e) => setField("shifts", e.target.value)} placeholder="örn. 2" />
              </div>
              <div className="form-field">
                <label className="lbl" htmlFor="lf-hps">Vardiya Başına Saat</label>
                <input id="lf-hps" className="form-input" type="text" inputMode="decimal" value={state.hoursPerShift} onChange={(e) => setField("hoursPerShift", e.target.value)} placeholder="örn. 8" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label className="lbl" htmlFor="lf-perf">Performans / OEE (%)</label>
                <input id="lf-perf" className="form-input" type="text" inputMode="decimal" value={state.perfPct} onChange={(e) => setField("perfPct", e.target.value)} placeholder="örn. 85" />
              </div>
              <div className="form-field">
                <label className="lbl" htmlFor="lf-maxwo">Eş Zamanlı İş Emri Limiti</label>
                <input id="lf-maxwo" className="form-input" type="text" inputMode="numeric" value={state.maxWo} onChange={(e) => setField("maxWo", e.target.value)} placeholder="örn. 3" />
              </div>
            </div>
            <div className="form-field">
              <label htmlFor="lf-stockprod" style={{ display: "flex", alignItems: "flex-start", gap: 9, cursor: "pointer", padding: "8px 10px", border: "1px solid var(--neutral-300)", borderRadius: "var(--radius-md)", background: state.allowStockProduction ? "var(--primary-subtle)" : "var(--surface, #fff)" }}>
                <input id="lf-stockprod" type="checkbox" checked={state.allowStockProduction} onChange={(e) => setField("allowStockProduction", e.target.checked)} style={{ marginTop: 2 }} />
                <span>
                  <span style={{ fontWeight: 600, fontSize: 13.5, color: "var(--neutral-900)" }}>Stoğa üretim izni</span>
                  <span style={{ display: "block", fontSize: 12, color: "var(--neutral-600)", marginTop: 2 }}>İşaretliyse bu merkez sipariş olmadan (make-to-stock) üretim yapabilir; bileşenler FEFO ile stoktan tüketilir.</span>
                </span>
              </label>
            </div>
            <div className="form-field">
              <label htmlFor="lf-blockshort" style={{ display: "flex", alignItems: "flex-start", gap: 9, cursor: "pointer", padding: "8px 10px", border: "1px solid var(--neutral-300)", borderRadius: "var(--radius-md)", background: state.blockShortageProduction ? "var(--warning-subtle)" : "var(--surface, #fff)" }}>
                <input id="lf-blockshort" type="checkbox" checked={state.blockShortageProduction} onChange={(e) => setField("blockShortageProduction", e.target.checked)} style={{ marginTop: 2 }} />
                <span>
                  <span style={{ fontWeight: 600, fontSize: 13.5, color: "var(--neutral-900)" }}>Eksik stok/yarı mamulde üretimi engelle</span>
                  <span style={{ display: "block", fontSize: 12, color: "var(--neutral-600)", marginTop: 2 }}>İşaretliyse, reçete bileşeni (yarı mamul veya stok) yetersizse parti üretimi engellenir. İşaretsizse mevcut stok düşülür, eksik kalan eksiye yazılır ve uyarı verilir.</span>
                </span>
              </label>
            </div>
            <div className="form-field">
              <label htmlFor="lf-autosemi" style={{ display: "flex", alignItems: "flex-start", gap: 9, cursor: "pointer", padding: "8px 10px", border: "1px solid var(--neutral-300)", borderRadius: "var(--radius-md)", background: state.autoConsumeSemi ? "var(--primary-subtle)" : "var(--surface, #fff)" }}>
                <input id="lf-autosemi" type="checkbox" checked={state.autoConsumeSemi} onChange={(e) => setField("autoConsumeSemi", e.target.checked)} style={{ marginTop: 2 }} />
                <span>
                  <span style={{ fontWeight: 600, fontSize: 13.5, color: "var(--neutral-900)" }}>Otomatik yarı mamul tüketimi</span>
                  <span style={{ display: "block", fontSize: 12, color: "var(--neutral-600)", marginTop: 2 }}>İşaretliyse, siparişe bağlı mamul üretiminde gerekli yarı mamul stoktan FEFO ile düşülür ve tüketilen stok yarı mamul partileri (siparişi/iş emri olmayan) otomatik o siparişe atanır.</span>
                </span>
              </label>
            </div>
            <div className="form-field">
              <label htmlFor="lf-autoprod" style={{ display: "flex", alignItems: "flex-start", gap: 9, cursor: "pointer", padding: "8px 10px", border: "1px solid var(--neutral-300)", borderRadius: "var(--radius-md)", background: state.autoProduceSemi ? "var(--primary-subtle)" : "var(--surface, #fff)" }}>
                <input id="lf-autoprod" type="checkbox" checked={state.autoProduceSemi} onChange={(e) => setField("autoProduceSemi", e.target.checked)} style={{ marginTop: 2 }} />
                <span>
                  <span style={{ fontWeight: 600, fontSize: 13.5, color: "var(--neutral-900)" }}>Otomatik yarı mamul üretimi</span>
                  <span style={{ display: "block", fontSize: 12, color: "var(--neutral-600)", marginTop: 2 }}>İşaretliyse, mamul üretilirken gereken yarı mamuller (alt reçeteler dahil) iş emirleriyle otomatik üretilir. "Otomatik tüketim" de açıksa önce stoktaki tüketilir, eksik kalan üretilip tüketilir; kapalıysa tam ihtiyaç kadar taze üretilir.</span>
                </span>
              </label>
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor="lf-cnotes">Merkez Notu</label>
              <textarea id="lf-cnotes" className="form-textarea" value={state.centerNotes} onChange={(e) => setField("centerNotes", e.target.value)} maxLength={500} rows={2} />
            </div>
            <span className="help">Bu merkezin altına "Üretim Hattı" tipinde alt lokasyonlar ekleyerek hatları tanımlayın.</span>
          </div>
        ) : null}
      </form>
    </Drawer>
  );
}
